import { ChangeDetectorRef, Component, DestroyRef, HostListener, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PaginatorModule } from 'primeng/paginator';
import { KeycloakService } from 'keycloak-angular';
import { AclApdApiService } from '@angular-workspace/services';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { FormatService } from '../../services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { canOpenConversation } from '../../services/conversation.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';
import { FilterPanelComponent, FilterField } from '../filter-panel/filter-panel.component';
import {
  ActionDropdownOverlayController,
} from '../../utils/action-dropdown-overlay.sync';

export interface AssetRequest {
  id: string;
  itemId?: string;
  name: string;
  type: string;
  requestType: string;
  purpose: string;
  organisation: string;
  requestAs?: 'Individual' | 'Organisation';
  requestCreatedAt: string;
  expiryAt?: string;
  status: 'approved' | 'rejected' | 'withdrawn' | 'pending';
}

export type RequestStatus = 'approved' | 'rejected' | 'withdrawn' | 'pending';
export type StatusFilter = RequestStatus | 'all';

@Component({
  selector: 'app-my-request-shared',
  standalone: true,
  imports: [CommonModule, PaginatorModule, ActionDropdownTriggerDirective, FilterPanelComponent],
  templateUrl: './my-request-shared.component.html',
  styleUrl: './my-request-shared.component.scss',
})
export class MyRequestSharedComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspaceContext = inject(WorkspaceContextService);
  /** Route prefix for the request conversation page; the action is hidden when unset. */
  @Input() conversationRoute: string | null = null;

  activeStatus: StatusFilter = 'all';
  loading = false;
  emptyMessage: SafeHtml = '';
  expandedRowKeys: { [key: string]: boolean } = {};

  filterFields: FilterField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'multiselect',
      placeholder: 'Choose Status',
      options: [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Withdrawn', value: 'withdrawn' },
      ],
      value: [{ label: 'All', value: 'all' }],
      maxSelected: 1,
      showClear: false,
    },
  ];

  requestsData: AssetRequest[] = [];
  openDropdownId: string | null = null;
  dropdownPosition: { top: number; left: number | null; right: number | null } = {
    top: 0,
    left: null,
    right: null,
  };
  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();
  private dropdownOpenedAt = 0;

  pagination = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    serverSide: true,
  };

  private readonly statusMap: Record<RequestStatus, string> = {
    approved: 'GRANTED',
    rejected: 'REJECTED',
    pending: 'PENDING',
    withdrawn: 'WITHDRAWN',
  };

  /** Statuses sent for initial load and the All filter (excludes granted/approved). */
  private readonly allFilterRequestStatuses = ['PENDING', 'WITHDRAWN', 'REJECTED'] as const;

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private aclApdApiService: AclApdApiService,
    private formatService: FormatService,
    private toasterService: ToasterService,
    private keycloak: KeycloakService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      // Granted Access (Approved Request / Issued policies) must not call access_request/consumer.
      if (params['tab'] === 'createdPolicies') {
        return;
      }

      const statusParam = params['status'];
      if (statusParam && this.isValidStatusFilter(statusParam)) {
        this.activeStatus = statusParam as StatusFilter;
      } else {
        this.activeStatus = 'all';
      }
      this.syncFilterFieldValue();
      this.expandedRowKeys = {};
      this.loadRequests(this.activeStatus);
    });
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;

      const maxWaitTime = 5000;
      const checkInterval = 100;
      const startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  private syncFilterFieldValue(): void {
    const selected =
      this.filterFields
        .find((field) => field.key === 'status')
        ?.options?.find((option) => option.value === this.activeStatus) ?? {
        label: 'All',
        value: 'all',
      };

    this.filterFields = this.filterFields.map((field) =>
      field.key === 'status' ? { ...field, value: [selected] } : field,
    );
  }

  private resolveStatusFromFilter(status: unknown): StatusFilter {
    if (Array.isArray(status)) {
      if (status.length === 0) return 'all';
      const first = status[0];
      const value =
        typeof first === 'object' && first !== null
          ? (first as { value?: string }).value
          : first;
      return typeof value === 'string' && this.isValidStatusFilter(value) ? value : 'all';
    }

    if (typeof status === 'string' && this.isValidStatusFilter(status)) {
      return status;
    }

    return 'all';
  }

  async loadRequests(status: StatusFilter, showLoading: boolean = true): Promise<void> {
    if (showLoading) this.loading = true;
    try {
      // Initial / All: only pending, withdrawn, rejected (granted lives on Granted Access).
      const requestStatus =
        status === 'all' ? [...this.allFilterRequestStatuses] : this.statusMap[status];

      const response = await this.aclApdApiService.getUserAccessRequests(
        this.pagination.currentPage,
        this.pagination.pageSize,
        requestStatus,
      );

      let requests: any[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        requests = response['result'];
      } else if (Array.isArray(response)) {
        requests = response;
      }

      const mappedRequests = requests.map((item: any) => {
        const requestAs = item.additionalInfo?.requestAs === 'Organisation' ? 'Organisation' : 'Individual';
        const organisationName =
          item.additionalInfo?.organisationName ||
          item.organisationName ||
          item.organisation_name ||
          item.orgName ||
          item.org_name ||
          item.organisation?.name ||
          item.organisation?.organisationName ||
          '';

        const mappedStatus =
          status === 'all'
            ? this.mapApiStatus(item.requestStatus || item.request_status || item.status)
            : status;

        return {
          id: item.id || item.requestId || '',
          itemId: item.asset?.itemId || item.itemId || '',
          name: item.asset?.assetName || item.assetName || item.name || '',
          type: item.asset?.assetType || item.assetType || item.type || '',
          requestType: this.toPascalCase(item.requestType || item.accessType || ''),
          purpose: item.additionalInfo?.purposeOfRequest || item.purpose || item.reason || '',
          organisation: organisationName || 'N/A',
          requestAs,
          requestCreatedAt: item.createdAt || item.created_at || '',
          expiryAt: item.expiryAt || item.expiry_at || item.expiresAt || item.expires_at || '',
          status: mappedStatus,
        } as AssetRequest;
      });

      // Approved/granted requests belong on the Granted Access tab — never show them here.
      this.requestsData =
        status === 'all'
          ? mappedRequests.filter((req) => req.status !== 'approved')
          : mappedRequests;

      const responseData = response as any;
      if (responseData && responseData['paginationInfo']) {
        this.pagination.totalItems = responseData['paginationInfo'].totalCount || mappedRequests.length;
      } else {
        this.pagination.totalItems = this.requestsData.length;
      }

      if (
        this.pagination.serverSide &&
        this.requestsData.length === 0 &&
        this.pagination.totalItems > 0
      ) {
        const previousPage = this.pagination.currentPage;
        this.clampCurrentPageToTotal();
        if (this.pagination.currentPage !== previousPage) {
          return this.loadRequests(status, showLoading);
        }
      }

      this.updateEmptyMessage(status);
    } catch (error) {
      console.error('Error loading access requests:', error);
      this.requestsData = [];
      this.updateEmptyMessage(status);
    } finally {
      if (showLoading) this.loading = false;
    }
  }

  private mapApiStatus(apiStatus: string): RequestStatus {
    const normalized = (apiStatus || '').toString().toUpperCase();
    if (normalized === 'GRANTED' || normalized === 'APPROVED') return 'approved';
    if (normalized === 'REJECTED') return 'rejected';
    if (normalized === 'WITHDRAWN') return 'withdrawn';
    return 'pending';
  }

  isValidStatusFilter(status: string): status is StatusFilter {
    // 'approved' is intentionally omitted — approved requests are shown on Granted Access.
    return ['all', 'rejected', 'withdrawn', 'pending'].includes(status);
  }

  onFilterChange(filters: Record<string, any>): void {
    if (filters['status'] === undefined) return;
    const nextStatus = this.resolveStatusFromFilter(filters['status']);
    if (nextStatus === this.activeStatus) return;
    this.filterByStatus(nextStatus);
  }

  onClearFilters(): void {
    this.filterByStatus('all');
  }

  filterByStatus(status: StatusFilter): void {
    this.activeStatus = status;
    this.syncFilterFieldValue();
    this.expandedRowKeys = {};
    this.pagination.currentPage = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status: status === 'all' ? null : status },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.loadRequests(status);
  }

  updateEmptyMessage(status: StatusFilter): void {
    const statusLabel = this.getActiveStatusLabel(status);
    const message =
      status === 'all'
        ? 'No requests found'
        : `No <span class="empty-message-status">${statusLabel}</span> requests found`;
    this.emptyMessage = this.sanitizer.bypassSecurityTrustHtml(message);
  }

  getAssetTypeIconSrc(type: string): string | null {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('databank') || lowerType.includes('dataset')) return '/asset-dataBank.svg';
    return null;
  }

  getAssetTypeIcon(type: string): SafeHtml {
    let iconPath = '';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('aimodel') || lowerType.includes('model') || lowerType.includes('ai')) {
      iconPath =
        '<path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    } else if (lowerType.includes('usecase') || lowerType.includes('use case') || lowerType.includes('apps')) {
      iconPath =
        '<path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    }
    return this.sanitizer.bypassSecurityTrustHtml(iconPath);
  }

  formatAssetTypeLabel(type: string): string {
    return this.formatService.formatAssetType(type);
  }

  get displayedRequests(): AssetRequest[] {
    if (!this.pagination.enabled) return this.requestsData;
    if (this.pagination.serverSide) return this.requestsData;
    const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
    const end = start + this.pagination.pageSize;
    return this.requestsData.slice(start, end);
  }

  get shouldShowPagination(): boolean {
    if (!this.pagination.enabled || this.pagination.totalItems === 0) return false;
    return this.pagination.totalItems > this.pagination.pageSize;
  }

  /** Keep current page within range after total item count drops (e.g. withdraw on last page). */
  private clampCurrentPageToTotal(): void {
    const { totalItems, pageSize } = this.pagination;
    if (totalItems <= 0) {
      this.pagination.currentPage = 1;
      return;
    }
    const maxPage = Math.max(1, Math.ceil(totalItems / pageSize));
    if (this.pagination.currentPage > maxPage) {
      this.pagination.currentPage = maxPage;
    }
  }

  onPageChange(event: any): void {
    this.closeActionDropdown();
    const page = Math.floor(event.first / event.rows) + 1;
    this.pagination.currentPage = page;
    this.loadRequests(this.activeStatus);
  }

  toggleActionDropdown(event: Event, row: AssetRequest): void {
    event.preventDefault();
    event.stopPropagation();
    const rowId = row.id;
    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }

    const button =
      (event.currentTarget as HTMLElement | null) ??
      ((event.target as HTMLElement).closest('.action-btn') as HTMLElement | null);
    if (!button) {
      return;
    }

    this.openDropdownId = rowId;
    this.dropdownOpenedAt = Date.now();
    this.actionDropdownOverlay.open({
      anchor: button,
      itemCount: 1 + (row.status === 'pending' ? 1 : 0) + (this.canConverse(row) ? 1 : 0),
      minWidth: 0,
      alignEnd: true,
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.detectChanges();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequest): boolean {
    return this.openDropdownId === row.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('.action-cell-wrapper') || target.closest('.action-dropdown-menu')) return;
    if (Date.now() - this.dropdownOpenedAt < 150) return;
    if (this.openDropdownId !== null) this.closeActionDropdown();
  }

  getAssetDetailRouteSegment(type: string): string | null {
    const lowerType = (type || '').toLowerCase();
    if (lowerType.includes('databank') || lowerType.includes('dataset') || lowerType.includes('database')) {
      return 'dataset';
    }
    if (lowerType.includes('aimodel') || lowerType.includes('model') || lowerType.includes('ai')) {
      return 'model';
    }
    if (lowerType.includes('usecase') || lowerType.includes('use case') || lowerType.includes('app')) {
      return 'usecase';
    }
    return null;
  }

  onActionClick(action: string, row: AssetRequest): void {
    this.closeActionDropdown();
    switch (action) {
      case 'view':
        if (row.itemId) {
          const routeSegment = this.getAssetDetailRouteSegment(row.type) || 'dataset';
          const url = this.router.serializeUrl(
            this.router.createUrlTree([`/${routeSegment}`, row.itemId])
          );
          window.open(url, '_blank');
        } else {
          console.warn('ItemId not available for request:', row);
        }
        break;
      case 'conversation':
        this.openConversation(row);
        break;
      case 'withdraw':
        this.handleWithdraw(row);
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  }

  canConverse(row: AssetRequest): boolean {
    if (this.workspaceContext.isDelegateSession()) return false;
    return !!this.conversationRoute && !!row.id && canOpenConversation(row.status, row.expiryAt);
  }

  private openConversation(row: AssetRequest): void {
    void this.router.navigate([this.conversationRoute, row.id, 'conversation'], {
      state: { assetName: row.name, purpose: row.purpose, status: row.status },
    });
  }

  async handleWithdraw(request: AssetRequest): Promise<void> {
    if (request.status !== 'pending' || !request.id) return;

    if (this.expandedRowKeys[request.id]) {
      const newKeys = { ...this.expandedRowKeys };
      delete newKeys[request.id];
      this.expandedRowKeys = newKeys;
    }

    const originalRequests = [...this.requestsData];
    const originalTotalItems = this.pagination.totalItems;
    const originalPage = this.pagination.currentPage;

    if (this.pagination.totalItems > 0) this.pagination.totalItems--;
    this.clampCurrentPageToTotal();

    const pageChanged = this.pagination.currentPage !== originalPage;
    if (!pageChanged) {
      this.requestsData = this.requestsData.filter((req) => req.id !== request.id);
    }

    try {
      await this.aclApdApiService.withdrawRequest(request.id);
      this.toasterService.success("You've Successfully withdrawn your request");
      await this.loadRequests(this.activeStatus, false);
    } catch (error: any) {
      console.error('Error withdrawing request:', error);
      if (error?.error?.detail || error?.detail) {
        const errorMessage = error?.error?.detail || error?.detail;
        this.toasterService.error(errorMessage);
      }
      this.requestsData = originalRequests;
      this.pagination.totalItems = originalTotalItems;
      this.pagination.currentPage = originalPage;
      this.loading = false;
    }
  }

  toPascalCase(str: string): string {
    if (!str) return '';
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private usesExpiryDate(status: RequestStatus | StatusFilter): boolean {
    return status === 'approved';
  }

  formatDatePart(dateString: string): string {
    const formatted = this.formatService.formatDate(
      dateString,
      this.usesExpiryDate(this.activeStatus),
    );
    if (!formatted) {
      return this.formatService.formatDateOnly(dateString);
    }
    return formatted.split(' ')[0];
  }

  formatTimePart(dateString: string): string {
    const formatted = this.formatService.formatDate(
      dateString,
      this.usesExpiryDate(this.activeStatus),
    );
    if (!formatted) {
      return this.formatService.formatTimeOnly(dateString);
    }
    return formatted.split(' ')[1] ?? '';
  }

  get dateColumnLabel(): string {
    return this.usesExpiryDate(this.activeStatus) ? 'Expiry At' : 'Request Created At';
  }

  getDateForStatus(row: AssetRequest): string {
    if (this.activeStatus === 'approved') {
      return row.expiryAt || row.requestCreatedAt;
    }
    return row.requestCreatedAt;
  }

  getActiveStatusLabel(status: StatusFilter = this.activeStatus): string {
    const labels: Record<StatusFilter, string> = {
      all: 'My Requests',
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn',
    };
    return labels[status];
  }

  getStatusLabel(status: RequestStatus): string {
    const labels: Record<RequestStatus, string> = {
      pending: 'Pending',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn',
      approved: 'Approved',
    };
    return labels[status] || 'Pending';
  }

  getStatusChipClass(status: RequestStatus): string {
    if (status === 'approved') return 'status-chip status-chip--approved';
    if (status === 'pending') return 'status-chip status-chip--pending';
    if (status === 'rejected') return 'status-chip status-chip--rejected';
    if (status === 'withdrawn') return 'status-chip status-chip--withdrawn';
    return 'status-chip status-chip--withdrawn';
  }

  toggleExpansion(requestId: string): void {
    if (this.expandedRowKeys[requestId]) {
      const newKeys = { ...this.expandedRowKeys };
      delete newKeys[requestId];
      this.expandedRowKeys = newKeys;
    } else {
      this.expandedRowKeys = { ...this.expandedRowKeys, [requestId]: true };
    }
  }

  isRowExpanded(requestId: string): boolean {
    return !!this.expandedRowKeys[requestId];
  }
}
